import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Paper,
    Typography,
    useTheme,
} from '@mui/material';
import { HEADER, NAV } from 'src/layouts/config-layout';
import { useSettingsContext } from 'src/components/settings';
import { FlagIcon } from 'src/components/iconify/flagIcon';
import HorizontalPager from 'src/components/horizontal-pager';
import { CountryFilterProps, CountryItem } from '../types';

export function CountryFilter({
    countries,
    selectedCountry,
    onCountryChange,
    backgroundColor = 'transparent',
    enableDeepLinking = true,
    renderCountryLabel,
    stickyTop,
}: CountryFilterProps & { stickyTop?: number }) {
    const theme = useTheme();
    const settings = useSettingsContext();
    const navWidth = settings.themeLayout === 'mini' ? NAV.W_MINI : NAV.W_VERTICAL;

    // Map region names to country codes
    const getCountryCodeFromRegionName = (regionName: string, countries: any[]): string | null => {
        if (!countries || countries.length === 0) return null;

        // Try exact match first
        const exactMatch = countries.find(
            (c) => c.country_name?.toLowerCase() === regionName?.toLowerCase()
        );
        if (exactMatch) return exactMatch.country_code;

        // Try partial match (e.g., "France" matches "France")
        const partialMatch = countries.find(
            (c) => regionName?.toLowerCase().includes(c.country_name?.toLowerCase()) ||
                c.country_name?.toLowerCase().includes(regionName?.toLowerCase())
        );
        if (partialMatch) return partialMatch.country_code;

        // Fallback: try to extract code from region name if it looks like a code
        if (regionName && regionName.length === 2) {
            return regionName.toUpperCase();
        }

        return null;
    };


    if (countries.length <= 1) {
        return null;
    }

    const codeToName = useMemo(() => {
        const map = new Map<string, string>();
        countries.forEach((c) => {
            map.set(String(c.country_code).toUpperCase(), c.country_name);
        });
        return map;
    }, [countries]);

    const [didApplyDeepLink, setDidApplyDeepLink] = useState(false);

    useEffect(() => {
        if (didApplyDeepLink || !enableDeepLinking) return;
        try {
            const params = new URLSearchParams(window.location.search);
            const codeParam = params.get('country');
            if (codeParam) {
                const countryName = codeToName.get(codeParam.toUpperCase());
                if (countryName) {
                    onCountryChange(selectedCountry === countryName ? selectedCountry : countryName);
                }
            }
        } catch { }
        setDidApplyDeepLink(true);
    }, [codeToName, onCountryChange, didApplyDeepLink, selectedCountry, enableDeepLinking]);

    return (
        <>
            <Box sx={{ height: 44 }} />
            <Paper sx={{ p: 0, overflow: 'hidden', borderBottom: "1px solid rgba(145, 158, 171, 0.20)", px: '24px', pt: 1, position: 'fixed', top: stickyTop ?? HEADER.H_DESKTOP * 2, left: { lg: navWidth }, right: 0, zIndex: theme.zIndex.appBar - 1 }}>
                <HorizontalPager
                spacing={20}
                items={countries}
                getItemKey={(c) => c.country_code}
                activeIndex={selectedCountry ? countries.findIndex((c) => c.country_name === selectedCountry) : null}
                renderItem={(c: CountryItem) => {
                    const countryCode = getCountryCodeFromRegionName(
                        c.country_name,
                        countries
                    );

                    return (
                        <Button
                            key={c.country_code}
                            variant="text"
                            color="inherit"
                            disableRipple
                            disableFocusRipple
                            disableTouchRipple
                            onClick={() =>
                                onCountryChange(
                                    selectedCountry === c.country_name ? null : c.country_name
                                )
                            }
                            sx={{
                                px: 0,
                                gap: 0.75,
                                minHeight: 'unset',
                                borderRadius: 0,
                                pb: 1,
                                borderBottom: selectedCountry === c.country_name ? '2px solid black' : '2px solid transparent',
                                transition: 'border-color 0.2s ease',
                                '&:hover': { backgroundColor: 'transparent', opacity: 0.8 },
                            }}
                        >
                            {countryCode && <FlagIcon code={countryCode} />}

                            <Typography
                                sx={{
                                    fontWeight: 400,
                                    fontSize: ".875rem",
                                }}
                                color={selectedCountry === c.country_name ? "black" : "#919EAB"}
                            >
                                {renderCountryLabel
                                    ? renderCountryLabel(c, selectedCountry === c.country_name)
                                    : c.country_name}
                            </Typography>

                        </Button>
                    );
                }}
            />
        </Paper>
        </>
    );
}
